import { adminPost } from "@/lib/kbAdminClient";
import { adviseFromChunkQuality, type ChunkQuality } from "./kbAdminAiHelpers";
import type { PanelNote } from "./types";

type ShowNote = (note: PanelNote | null) => void;

type Params = {
  hasEditorName: boolean;
  broaderScopeLocked: boolean;
  orgId?: string | null;
  articleMode: "preview" | "save";
  articleSlug: string;
  articleTitle: string;
  articleContent: string;
  articleAudience: string;
  articleScope: string;
  articleDocType: string;
  articleTagsInput: string;
  articleSourceUrl: string;
  articleStatus: string;
  editorName: string;
  loadedArticle: any;
  setChunkQuality: (v: ChunkQuality | null) => void;
  setChunkQualityAdvice: (v: string[]) => void;
  setLastArticleId: (v: string) => void;
  setAiHelpOpen: (v: boolean) => void;
  setSplitProposals: (v: any[]) => void;
  setSplitOpen: (v: boolean) => void;
  refreshPendingProposalCount: () => Promise<void>;
  refreshDraftCount: () => Promise<void>;
  resetEditorAfterSave: () => void;
  showNote: ShowNote;
};

export function useKbArticleSaveWorkflow(p: Params) {
  async function doArticle() {
    p.showNote(null);
    if (!p.hasEditorName) return p.showNote({ tone: "error", title: "Profile required", text: "Complete your signed-in profile name in Settings before editing the Knowledge Base." });
    if (p.broaderScopeLocked && p.articleMode === "save") return p.showNote({ tone: "error", title: "Scope locked", text: "Only platform admin or platform dev users can update app-level or manufacturer-level Knowledge Base documents." });
    const tags = p.articleTagsInput.split(",").map((tag) => tag.trim()).filter(Boolean);
    if (p.articleScope === "org" && !p.orgId) return p.showNote({ tone: "error", title: "Missing org ID", text: "Cannot save an org-scoped article without an organization ID." });

    const runSave = (confirmPolicyOverwrite: boolean, confirmPolicyConflictReview: boolean, forcePublish: boolean) =>
      adminPost<any>("/api/kb/admin/article", {
        mode: p.articleMode, slug: p.articleSlug || undefined, title: p.articleTitle, content: p.articleContent, audience: p.articleAudience, scope: p.articleScope,
        doc_type: p.articleDocType || null, tags, locations: [], sourceUrl: p.articleSourceUrl || null, status: p.articleStatus, organizationId: p.articleScope === "org" ? p.orgId : undefined,
        expectedVersion: p.articleMode === "save" && p.loadedArticle?.version != null ? Number(p.loadedArticle.version) : undefined, chunkSize: 500, overlap: 50, editedBy: p.editorName,
        ...(confirmPolicyOverwrite ? { confirmPolicyOverwrite: true } : {}), ...(confirmPolicyConflictReview ? { confirmPolicyConflictReview: true } : {}), ...(forcePublish ? { forcePublish: true } : {}),
      });

    let confirmedPolicyOverwrite = false;
    let confirmedPolicyConflictReview = false;
    let forcePublish = false;
    let response = await runSave(confirmedPolicyOverwrite, confirmedPolicyConflictReview, forcePublish);

    if (!response.ok && response.code === "POLICY_OVERWRITE_CONFIRM" && p.articleMode === "save" && window.confirm("This article is classified as policy. Saving will replace the stored policy content for everyone using this KB. Continue?")) {
      confirmedPolicyOverwrite = true;
      response = await runSave(confirmedPolicyOverwrite, confirmedPolicyConflictReview, forcePublish);
    }

    if (!response.ok && response.code === "POLICY_CONFLICT_REVIEW_REQUIRED" && p.articleMode === "save") {
      const conflicts = Array.isArray(response.conflicts) ? response.conflicts : [];
      const listed = conflicts.map((item: any) => `- ${item?.title || item?.slug || "Untitled"} (${item?.slug || "no-slug"})`).join("\n");
      const confirmMsg = "This policy may duplicate or conflict with existing policy docs:\n\n" + `${listed || "- Existing policy overlap detected"}\n\n` + "Continue anyway? Use this only if this new article intentionally supersedes or consolidates those docs.";
      if (window.confirm(confirmMsg)) {
        confirmedPolicyConflictReview = true;
        response = await runSave(confirmedPolicyOverwrite, confirmedPolicyConflictReview, forcePublish);
      }
    }

    if (!response.ok && response.code === "LOW_CHUNK_QUALITY" && p.articleMode === "save") {
      const quality = (response.detail as { chunkQuality?: ChunkQuality } | undefined)?.chunkQuality;
      if (quality) {
        p.setChunkQuality(quality);
        p.setChunkQualityAdvice(adviseFromChunkQuality(quality));
      }
      if (window.confirm(`Chunk quality is ${quality?.risk ?? "low"} (${quality?.totalChunkChars ?? 0} total chars). Publish anyway?`)) {
        forcePublish = true;
        response = await runSave(confirmedPolicyOverwrite, confirmedPolicyConflictReview, forcePublish);
      }
    }

    if (!response.ok) {
      p.showNote({ tone: "error", title: p.articleMode === "save" ? "Save failed" : "Preview failed", text: response.status === 409 ? "Version conflict. Reload the article before saving again." : response.error });
      return;
    }

    if (p.articleMode === "preview") {
      const quality = response.data?.chunkQuality as ChunkQuality | undefined;
      if (quality) {
        p.setChunkQuality(quality);
        p.setChunkQualityAdvice(adviseFromChunkQuality(quality));
      }
      p.showNote({ tone: "success", title: "Preview ready", text: `Chunk preview generated with ${response.data?.chunks?.length ?? "?"} chunk(s).` });
      return;
    }

    if (response.data?.mode === "proposal") {
      await p.refreshPendingProposalCount();
      p.showNote({ tone: "success", title: "Change submitted for review", text: response.data?.message || "An admin can approve this update from the Change proposals panel. Your edits are not live until approved." });
      return;
    }

    p.setLastArticleId(response.data?.articleId || "");
    const quality = response.data?.chunkQuality as ChunkQuality | undefined;
    if (quality) {
      p.setChunkQuality(quality);
      p.setChunkQualityAdvice(adviseFromChunkQuality(quality));
    }
    await p.refreshDraftCount();
    p.resetEditorAfterSave();
    p.setAiHelpOpen(false);
    p.setSplitProposals([]);
    p.setSplitOpen(false);
    p.showNote({ tone: "success", title: "Article saved", text: `Saved "${response.data?.article?.title || p.articleTitle}". Version ${response.data?.version ?? "?"}.` });
  }

  return { doArticle };
}
